/**
 * ContactInfoBanner — info-level banner with property contact details so
 * renters who had a failed file upload can reach out for help.
 * Only renders when propertyContact data is available (cert-scoped uploads).
 */
import React, { JSX, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import type { PropertyContact } from 'src/types/aiFileUploadV2';
import { formatPhoneNumber } from 'src/utils/formatNumber';

interface ContactInfoBannerProps {
  contact: PropertyContact;
}

export function ContactInfoBanner({
  contact,
}: ContactInfoBannerProps): JSX.Element | null {
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  const hasEmail = contact.property_email.trim().length > 0;
  const hasPhone = contact.contact_phone_number.trim().length > 0;

  if (!hasEmail && !hasPhone) {
    return (
      <Box
        sx={{
          background: theme.palette.info.lighter,
          border: `1px solid ${theme.palette.info.light}`,
          borderRadius: '10px',
          padding: '10px 12px',
          mb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
        }}
        data-testid="contact-info-banner"
      >
        <Typography
          sx={{ flex: 1, fontSize: 12, color: theme.palette.info.dark }}
        >
          Need help? Please contact us for assistance with your upload.
        </Typography>
        <IconButton
          size="small"
          aria-label="Dismiss contact info"
          onClick={() => setDismissed(true)}
          sx={{ color: theme.palette.info.dark, p: 0.25, flexShrink: 0 }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  }

  // Sanitize/encode untrusted values before building the sms: URI — a phone
  // value like "5551234567?body=..." must not inject its own query.
  const smsPhone = contact.contact_phone_number.replace(/[^\d+]/g, '');
  const smsBody = encodeURIComponent(
    'My document was rejected when uploading it to the platform'
  );

  return (
    <Box
      sx={{
        background: theme.palette.info.lighter,
        border: `1px solid ${theme.palette.info.light}`,
        borderRadius: '10px',
        padding: '10px 12px',
        mb: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
      }}
      data-testid="contact-info-banner"
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 12, color: theme.palette.info.dark, mb: 0.5 }}
        >
          If you think your document was valid, <b>please contact us</b> for
          assistance.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {hasEmail && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.palette.info.dark,
                  fontWeight: 'bold',
                }}
              >
                Email:
              </Typography>
              <Button
                href={`mailto:${contact.property_email}?subject=Document rejected when uploading it to the platform`}
                variant="contained"
                size="small"
                color={'info.dark'}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  borderColor: theme.palette.info.dark,
                  textTransform: 'none',
                  py: 0.25,
                  px: 1.5,
                }}
              >
                {contact.property_email}
              </Button>
            </Box>
          )}
          {hasPhone && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.palette.info.dark,
                  fontWeight: 'bold',
                }}
              >
                Phone:
              </Typography>
              <Button
                href={`sms:${smsPhone}?body=${smsBody}`}
                variant="contained"
                size="small"
                color={'info.dark'}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  borderColor: theme.palette.info.dark,
                  textTransform: 'none',
                  py: 0.25,
                  px: 1.5,
                }}
              >
                {formatPhoneNumber(contact.contact_phone_number)}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <IconButton
        size="small"
        aria-label="Dismiss contact info"
        onClick={() => setDismissed(true)}
        sx={{ color: theme.palette.info.dark, p: 0.25, flexShrink: 0 }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}

export default ContactInfoBanner;
